import { useState, useEffect, Fragment, useRef } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { ReactSortable } from "react-sortablejs";
import { Table } from "reactstrap";
import { useDraggable } from "react-use-draggable-scroll";
//
import TooltipCustom from "./TooltipCustom";

interface TableSortableProps<T> {
    className?: any;
    tableClass?: any;
    theadClass?: any;
    trClass?: any;
    thClass?: any;
    divClass?: any;
    sorting?: { sort_by: string, order_by: string };
    handleChangeSorting?: (sortBy: { sort_by: string | undefined | null, order_by: string | undefined | null }) => void,
    isLoading?: boolean,
    isShowLoadingBottom?: boolean,
    classBackgroundWhenActiveSort?: string,
    idTableName?: string,
    defaultScrollBottom?: boolean
    columns: any[];
    data: T[];
    customPageCount?: number;
    onOrderChange?: (newData: T[]) => void;
    handleClassName?: string;
    lockedClassName?: string;
    customPageIndex?: number
}

function TableDragContainer<T extends { id: string | number }>({
    columns = [],
    data = [],
    tableClass,
    theadClass,
    trClass,
    thClass,
    divClass,
    customPageCount,
    sorting,
    handleChangeSorting,
    isLoading = false,
    isShowLoadingBottom = false,
    classBackgroundWhenActiveSort = '',
    defaultScrollBottom = false,
    customPageIndex = 0,
    onOrderChange,
    handleClassName = "my-handle",
    lockedClassName = "locked_item",
}: TableSortableProps<T>) {
    const [isScrollBottom, setIsScrollBottom] = useState<boolean>(false);
    const [scrollLeft, setScrollLeft] = useState<any>(0);
    const thRefs = useRef<any>([]);
    const tableRef = useRef<any>(null);
    const warperTableRef = useRef<any>(null);
    const elementFixedRef = useRef<any>(null);
    const elementHeaderFixedRef = useRef<any>(null);
    const elementTable: any = tableRef?.current?.getBoundingClientRect() || {};
    const elementWarperTable: any = warperTableRef?.current?.getBoundingClientRect() || {};
    const listRef = useRef<T[]>([]);
    const [list, setList] = useState<T[]>(() => data.map((item) => ({ ...item })));
    const [isShowHeaderTableFixed, setIsShowHeaderTableFixed] = useState<boolean>(false); // Fixed Header Table

    const { events } = useDraggable(warperTableRef);

    const generateSortingIndicator = (column: any) => {
        return (
            column?.sortable ? (
                <span style={{ display: 'inline-flex', justifyContent: 'center', width: '12px', marginLeft: '2px', verticalAlign: 'middle' }}>
                    {(column.id === sorting?.sort_by && sorting?.order_by === 'ASC') && <i className={`ri-arrow-up-line fs-13 text-primary`} />}
                    {(column.id === sorting?.sort_by && sorting?.order_by === 'DESC') && <i className={`ri-arrow-down-line fs-13 text-primary`} />}
                </span>
            ) : (
                ""
            )
        );
    };
    const generateDescription = (column: any) => {
        return (
            column?.description ? (
                <TooltipCustom
                    title={column?.description || ''}
                    className="d-inline-block vertical-align-middle ms-1"
                    style={{ transform: 'translateY(2px)' }}
                >
                    <i className="ri-question-line align-bottom text-secondary" ></i>
                </TooltipCustom>
            ) : (<></>)
        );
    };

    const { getTableProps, headerGroups, getTableBodyProps, prepareRow, page } =
        useTable(
            {
                columns,
                data: list,
                initialState: { pageIndex: customPageIndex },
                manualPagination: true,
                pageCount: customPageCount,
            },
            useSortBy,
            usePagination
        );

    const handleSetList = (newList: T[]) => {
        listRef.current = newList;
        setList(newList);
    };

    const handleScroll = (e: any) => {
        setScrollLeft((prev: any) => e?.target?.scrollLeft)
        if (warperTableRef.current && elementFixedRef.current) {
            // If warperTableRef scrolls, then scroll elementFixedRef as well
            if (e.target === warperTableRef.current) {
                elementFixedRef.current.scrollLeft = e?.target?.scrollLeft;
                elementHeaderFixedRef.current.scrollLeft = e?.target?.scrollLeft; // Fixed Header Table
            }
            // If elementFixedRef scrolls, then scroll warperTableRef as well
            else if (e.target === elementFixedRef.current) {
                warperTableRef.current.scrollLeft = e?.target?.scrollLeft;
            }
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document?.documentElement?.scrollTop || 0;
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const MARGIN_BOTTOM_HEIGHT = 170; // 170px
            if ((scrollTop + windowHeight + MARGIN_BOTTOM_HEIGHT) >= documentHeight) {
                setIsScrollBottom((prev) => true);
            } else {
                setIsScrollBottom((prev) => false);
            }

            // Begin::Fixed Header Table
            const rect = warperTableRef.current.getBoundingClientRect();
            const distanceFromTop = rect.top;
            if (distanceFromTop <= 70) {
                elementHeaderFixedRef.current.scrollLeft = elementFixedRef?.current?.scrollLeft; // Fixed Header Table
                setIsShowHeaderTableFixed((prev) => true);
            } else {
                setIsShowHeaderTableFixed((prev) => false);
            }
            // End::Fixed Header Table

        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const draggedElementRef = { current: null as HTMLElement | null };
        let rafId: number;

        const handleDragStart = (event: DragEvent) => {
            const target = event.target as HTMLElement;
            if (
                target?.classList.contains("sortable-drag") 
            ) {
                draggedElementRef.current = target;
            } else {
                draggedElementRef.current = null;
            }
        };

        const handleDragEnd = () => {
            draggedElementRef.current = null;
            if (rafId) cancelAnimationFrame(rafId);
        };

        const handleDragOver = (event: DragEvent) => {
            if (!draggedElementRef.current) return;
            event.preventDefault();

            const mouseY = event.clientY; 
            const windowHeight = window.innerHeight;
            const scrollThreshold = 300;
            const scrollSpeed = 10;

            if (rafId) cancelAnimationFrame(rafId);

            rafId = requestAnimationFrame(() => {
                if (mouseY < scrollThreshold) {
                    window.scrollBy({ top: -scrollSpeed });
                } else if (mouseY > windowHeight - scrollThreshold) {
                    window.scrollBy({ top: scrollSpeed });
                }
            });
        };

        document.body.addEventListener("dragstart", handleDragStart);
        document.body.addEventListener("dragend", handleDragEnd);
        document.body.addEventListener("dragover", handleDragOver);

        return () => {
            document.body.removeEventListener("dragstart", handleDragStart);
            document.body.removeEventListener("dragend", handleDragEnd);
            document.body.removeEventListener("dragover", handleDragOver);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    useEffect(() => {
        setIsScrollBottom(defaultScrollBottom)
    }, [defaultScrollBottom])

    useEffect(() => {
        setList(data.map((item) => ({ ...item })));
    }, [data]);

    return (
        <Fragment>
            <div className={`table-custom-pagination ${divClass}`} {...events} ref={warperTableRef} onScroll={handleScroll}>
                <Table hover {...getTableProps()} className={`${tableClass} ${Number(scrollLeft || 0) > 0 && 'is-scroll'}`} style={{ minHeight: '70px' }} >
                    <thead className={theadClass} ref={tableRef}>
                        {headerGroups.map((headerGroup: any) => (
                            <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: any, i: number) => (
                                    <th
                                        key={column.id}
                                        ref={el => thRefs.current[i] = el} // Fixed Header Table
                                        className={`${thClass || ''} ${column?.thClass || ''} ${(column?.sortable && column.id === sorting?.sort_by) && classBackgroundWhenActiveSort}`}
                                        {...(column?.thWidth ? { width: column?.thWidth } : {})}
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        onClick={(e) => {

                                            if (!column?.sortable) { return };

                                            if ((!sorting?.sort_by || !sorting?.order_by)) {
                                                const sortBy = { sort_by: column?.id, order_by: 'DESC' };
                                                handleChangeSorting && handleChangeSorting(sortBy);
                                            } else if (sorting?.sort_by && sorting?.order_by) {
                                                const sortBy = { sort_by: column?.id, order_by: sorting?.order_by === 'DESC' && sorting?.sort_by === column?.id ? 'ASC' : 'DESC' };
                                                handleChangeSorting && handleChangeSorting(sortBy);
                                            }
                                        }}
                                    >

                                        {column?.thComponent ? <>{column?.thComponent(column)}</> : column.render("Header")}
                                        {generateDescription(column)}
                                        {generateSortingIndicator(column)}

                                    </th>
                                ))}
                            </tr>
                        ))}
                        {(headerGroups?.length) && (<tr >
                            <th colSpan={headerGroups[0]?.headers?.length} className="py-0 px-0" style={{ borderBottomColor: 'transparent' }}>
                                <div className={`infinite-loading-outer ${isLoading && 'is-show-loading'}`}>
                                    <div className="infinite-loading-inner"></div>
                                </div>
                            </th>
                        </tr>)}
                    </thead>

                    <ReactSortable
                        animation={200}
                        delay={2}
                        tag="tbody"
                        list={list}
                        setList={handleSetList}
                        onEnd={() => {
                            onOrderChange?.(listRef.current);
                        }}
                        handle={`.${handleClassName}`}
                        filter={`.${lockedClassName}`}
                        chosenClass="bg-white"
                        preventOnFilter={false}
                        onMove={(evt) => !evt.related.classList.contains(lockedClassName)}
                        {...getTableBodyProps()}
                    >
                        {page?.map((row: any) => {
                            prepareRow(row);
                            return (
                                <Fragment key={row.getRowProps().key}>
                                    <tr className={trClass || ""}>
                                        {row.cells.map((cell: any) => {
                                            return (
                                                <td key={cell.id} {...cell.getCellProps()}>
                                                    {cell.render("Cell")}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </Fragment>
                            );
                        })}
                    </ReactSortable>
                </Table>
            </div>
            {/* Begin::Fixed Header Table */}
            <div
                className={`table-card overflow-x-auto m-0 hidden-scrollbar-os ${isShowHeaderTableFixed ? '' : 'd-none'}`}
                style={{
                    position: 'fixed',
                    top: 70,
                    zIndex: 9,
                    left: elementWarperTable?.left,
                    width: elementWarperTable?.width,
                    right: 0,

                }}
                ref={elementHeaderFixedRef}
            >
                <Table hover {...getTableProps()} className={`${tableClass} ${Number(scrollLeft || 0) > 0 && 'is-scroll'} mb-0`} style={{ height: 'auto', tableLayout: 'fixed' }} >
                    <thead className={theadClass} style={{ width: elementTable?.width }}>
                        {headerGroups.map((headerGroup: any) => (
                            <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: any, i: number) => (
                                    <th
                                        key={column.id}
                                        className={`${thClass || ''} ${column?.thClass || ''} ${(column?.sortable && column.id === sorting?.sort_by) && classBackgroundWhenActiveSort}`}
                                        {...(column?.thWidth ? { width: thRefs.current[i]?.offsetWidth } : { width: thRefs.current[i]?.offsetWidth })}
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        onClick={(e) => {

                                            if (!column?.sortable) { return };

                                            if ((!sorting?.sort_by || !sorting?.order_by)) {
                                                const sortBy = { sort_by: column?.id, order_by: 'DESC' };
                                                handleChangeSorting && handleChangeSorting(sortBy);
                                            } else if (sorting?.sort_by && sorting?.order_by) {
                                                const sortBy = { sort_by: column?.id, order_by: sorting?.order_by === 'DESC' && sorting?.sort_by === column?.id ? 'ASC' : 'DESC' };
                                                handleChangeSorting && handleChangeSorting(sortBy);
                                            }
                                        }}
                                    >

                                        {column?.thComponent ? <>{column?.thComponent()}</> : column.render("Header")}
                                        {generateDescription(column)}
                                        {generateSortingIndicator(column)}

                                    </th>
                                ))}
                            </tr>
                        ))}
                        {(headerGroups?.length) && (<tr >
                            <th colSpan={headerGroups[0]?.headers?.length} className="py-0 px-0" style={{ borderBottomColor: 'transparent' }}>
                                <div className={`infinite-loading-outer ${isLoading && 'is-show-loading'}`}>
                                    <div className="infinite-loading-inner"></div>
                                </div>
                            </th>
                        </tr>)}
                    </thead>
                </Table>
            </div>
            {/* End::Fixed Header Table */}

            <div
                className="overflow-x-auto color-scrollbar-os"
                style={{
                    position: 'fixed',
                    zIndex: 999,
                    left: elementWarperTable?.left,
                    bottom: 0,
                    width: elementWarperTable?.width,
                    right: 0,
                    opacity: isScrollBottom ? 0 : 1,
                }}
                ref={elementFixedRef}
                onScroll={handleScroll}
            >
                <div style={{ width: elementTable?.width, height: '1px' }}></div>
            </div>

            {(isShowLoadingBottom && isLoading) && (<div className=" d-flex text-center align-items-center justify-content-center pt-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="100px" height="60px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
                    <g transform="translate(20 50)">
                        <circle cx="0" cy="0" r="10" fill="#3498db">
                            <animateTransform attributeName="transform" type="scale" begin="-0.375s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
                        </circle>
                    </g><g transform="translate(40 50)">
                        <circle cx="0" cy="0" r="10" fill="#53a6de">
                            <animateTransform attributeName="transform" type="scale" begin="-0.25s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
                        </circle>
                    </g><g transform="translate(60 50)">
                        <circle cx="0" cy="0" r="10" fill="#5fb5ef">
                            <animateTransform attributeName="transform" type="scale" begin="-0.125s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
                        </circle>
                    </g><g transform="translate(80 50)">
                        <circle cx="0" cy="0" r="10" fill="#5ca6d8">
                            <animateTransform attributeName="transform" type="scale" begin="0s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
                        </circle>
                    </g>
                </svg>
            </div>)}
        </Fragment>
    );
}

export default TableDragContainer;
